import { useId, useState } from 'react';
import { localeLabels, type Locale } from '@sonasi/shared-types';

/**
 * What a search result and a shared link show for one page, and the boxes
 * to change it.
 *
 * ⚠️ **Blank is the ordinary state, and the design rests on that.** [Blog 5]
 * took a meta title and description *off* the post editor: two boxes for
 * the same sentence drift apart, the excerpt gets edited and the meta pair
 * does not, and the search result quietly shows last month's wording. These
 * boxes come back empty by default and show, as their placeholder and in
 * the previews, exactly what the page would say on its own. Typing into one
 * is a decision to say something else; "Reset to automatic" takes it back.
 *
 * The previews are the point of the card: a search result and a share card
 * are what the studio is editing, and the boxes are only how. Both show the
 * *effective* text — what was typed, or the automatic text — so the card
 * reads the same whether or not anything has been written.
 */

/** Past what a result shows (about 60 and 160), short of a paragraph. The server's ceilings. */
export const SEO_TITLE_LIMIT = 70;
export const SEO_DESCRIPTION_LIMIT = 200;
/** Where a result starts cutting; a mark on the counter, not a rule. */
const SHOWN_TITLE = 60;
const SHOWN_DESCRIPTION = 160;

export interface ShareImageControl {
  /** The picture chosen, or null for the drawn one. */
  current: string | null;
  /**
   * Where the website draws its own, for the preview while nothing is chosen
   * — or null while there is nothing to draw: the website answers 404 for a
   * page that is not public yet, and a broken image is not a preview.
   */
  generated: string | null;
  pending: boolean;
  error: string | null;
  onChange: (file: File | null) => void;
}

export interface SearchSharingProps {
  lang: Locale;
  /** Where the page is, as a search result and a card show it. */
  url: string;
  /** The words the page uses on its own. */
  auto: { title: string; description: string };
  /** The words written over them, in this language — blank for none. */
  value: { title: string; description: string };
  onChange: (patch: Partial<{ title: string; description: string }>) => void;
  image: ShareImageControl;
  /** For the site: no words to override, only the picture. */
  imageOnly?: boolean;
  /** More boxes, under the picture — the site's links. */
  children?: React.ReactNode;
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <span className="field-label">{children}</span>
);

const Counter = ({
  id,
  length,
  limit,
  shown,
}: {
  id: string;
  length: number;
  limit: number;
  /** Where a result starts cutting the text off. */
  shown: number;
}) => (
  <span
    id={id}
    aria-label={`${length} of ${limit} characters; a result shows about ${shown}`}
    className="ml-auto font-mono text-[9.5px]"
    style={{ color: length > shown ? 'var(--warn)' : 'var(--dim)' }}
  >
    {length} / {limit}
  </span>
);

/** "Automatic" while the box is blank; a way back to it once it is not. */
const Origin = ({ custom, onReset }: { custom: boolean; onReset: () => void }) =>
  custom ? (
    <button
      type="button"
      onClick={onReset}
      className="btn-quiet focus-ring cursor-pointer text-[10px]"
      style={{ color: 'var(--dim)' }}
    >
      Reset to automatic
    </button>
  ) : (
    <span
      className="rounded-full border px-1.5 font-mono text-[9px] uppercase"
      style={{ borderColor: 'var(--line2)', color: 'var(--dim)' }}
    >
      Automatic
    </span>
  );

/** `url` as a search result prints it: host, then the path as a trail. */
const breadcrumb = (url: string): string => {
  try {
    const parsed = new URL(url);
    const parts = parsed.pathname.split('/').filter(Boolean);
    return [parsed.host, ...parts].join(' › ');
  } catch {
    return url;
  }
};

const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

export function SearchSharingCard({
  lang,
  url,
  auto,
  value,
  onChange,
  image,
  imageOnly = false,
  children,
}: SearchSharingProps) {
  const ids = useId();
  const title = value.title.trim() || auto.title;
  const description = value.description.trim() || auto.description;
  const picture = image.current ?? image.generated;
  /**
   * The address that failed to load, if the one on screen did. The website
   * draws the automatic picture, and a website that is not running — or is
   * running somewhere other than `websiteUrl` says — answers with nothing;
   * so can a chosen picture whose file is gone. A broken-image icon says
   * none of that; naming the host says where to look.
   */
  const [failed, setFailed] = useState<string | null>(null);
  /** The page's title as the tab shows it — the layout adds the site's name. */
  const shownTitle = `${title} · Sonasi`;

  return (
    <div className="panel flex flex-col gap-3 px-4 py-3.5">
      <span className="flex flex-wrap items-baseline gap-2.5">
        <h2 className="eyebrow m-0">Search &amp; sharing</h2>
        {!imageOnly ? (
          <span className="font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            {localeLabels[lang]}
          </span>
        ) : null}
      </span>

      <div className="flex flex-wrap items-start gap-4">
        {/* ── What it looks like ── */}
        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-3">
          {!imageOnly ? (
            <figure
              aria-label="Search result preview"
              className="m-0 flex flex-col gap-0.5 rounded-[9px] border px-3 py-2.5"
              style={{ borderColor: 'var(--line)', background: 'var(--in)' }}
            >
              <span
                className="flex items-center gap-2 text-[11px]"
                style={{ color: 'var(--muted)' }}
              >
                <span
                  aria-hidden="true"
                  className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold"
                  style={{ background: 'var(--panel2)', color: 'var(--text)' }}
                >
                  S
                </span>
                <span className="flex flex-col leading-tight">
                  <span style={{ color: 'var(--text)' }}>Sonasi</span>
                  <span className="truncate text-[10px]">{breadcrumb(url)}</span>
                </span>
              </span>
              <span
                data-testid="preview-title"
                className="truncate text-[15px] leading-snug"
                style={{ color: 'var(--accent)' }}
              >
                {shownTitle}
              </span>
              <span
                data-testid="preview-description"
                className="line-clamp-2 text-[12px] leading-[1.45]"
                style={{ color: 'var(--muted)' }}
              >
                {description}
              </span>
            </figure>
          ) : null}

          <figure
            aria-label="Share card preview"
            // As wide as the result above it: two previews of one page, one column.
            className="m-0 flex w-full flex-col overflow-hidden rounded-[9px] border"
            style={{ borderColor: 'var(--line)', background: 'var(--in)' }}
          >
            {/*
              `alt=""`: the figure's label says what this is, and the picture
              is the thing being previewed rather than something to describe.
              (An alt would also make it a picture to every test that counts
              the screenshots on the page.)
            */}
            {picture && failed !== picture ? (
              <img
                src={picture}
                alt=""
                onError={() => setFailed(picture)}
                className="block aspect-[1200/630] w-full object-cover"
                style={{ background: 'var(--panel2)' }}
              />
            ) : picture ? (
              <span
                role="status"
                className="flex aspect-[1200/630] w-full flex-col items-center justify-center gap-1 px-4 text-center font-mono text-[10px]"
                style={{ background: 'var(--panel2)', color: 'var(--dim)' }}
              >
                <span>Preview unavailable</span>
                <span>could not load it from {hostOf(picture)}</span>
              </span>
            ) : (
              <span
                className="flex aspect-[1200/630] w-full items-center justify-center font-mono text-[10px]"
                style={{ background: 'var(--panel2)', color: 'var(--dim)' }}
              >
                Drawn once the page is public
              </span>
            )}
            <span className="flex flex-col gap-0.5 px-3 py-2">
              <span
                className="font-mono text-[9.5px] tracking-wide uppercase"
                style={{ color: 'var(--dim)' }}
              >
                {hostOf(url)}
              </span>
              <span className="truncate text-[12.5px] font-semibold">{title}</span>
            </span>
          </figure>
          <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
            {imageOnly
              ? 'What a link to the home page, the catalogue, the blog and the support and legal pages unfurls with.'
              : 'Roughly what Google shows and what a link unfurls with on X, Facebook and in messages. Blank boxes mean the page speaks for itself.'}
          </span>
        </div>

        {/* ── The boxes ── */}
        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-3">
          {!imageOnly ? (
            <>
              {/*
                A `div` with a `label htmlFor`, not a `label` around the lot:
                a button is a labelable element, and a label wrapping the
                "Reset" button before the box would label the button.
              */}
              <div className="flex flex-col gap-[5px]">
                <span className="flex items-baseline gap-2">
                  <label htmlFor={`${ids}-title`} className="field-label">
                    Search title
                  </label>
                  <Origin
                    custom={value.title.trim() !== ''}
                    onReset={() => onChange({ title: '' })}
                  />
                  <Counter
                    id={`${ids}-title-count`}
                    length={value.title.length}
                    limit={SEO_TITLE_LIMIT}
                    shown={SHOWN_TITLE}
                  />
                </span>
                <input
                  id={`${ids}-title`}
                  value={value.title}
                  placeholder={auto.title}
                  maxLength={SEO_TITLE_LIMIT}
                  aria-describedby={`${ids}-title-count`}
                  onChange={(event) => onChange({ title: event.target.value })}
                  className="field"
                />
              </div>
              <div className="flex flex-col gap-[5px]">
                <span className="flex items-baseline gap-2">
                  <label htmlFor={`${ids}-description`} className="field-label">
                    Search description
                  </label>
                  <Origin
                    custom={value.description.trim() !== ''}
                    onReset={() => onChange({ description: '' })}
                  />
                  <Counter
                    id={`${ids}-description-count`}
                    length={value.description.length}
                    limit={SEO_DESCRIPTION_LIMIT}
                    shown={SHOWN_DESCRIPTION}
                  />
                </span>
                <textarea
                  id={`${ids}-description`}
                  rows={3}
                  value={value.description}
                  placeholder={auto.description}
                  maxLength={SEO_DESCRIPTION_LIMIT}
                  aria-describedby={`${ids}-description-count`}
                  onChange={(event) => onChange({ description: event.target.value })}
                  className="field resize-y leading-[1.55]"
                />
              </div>
            </>
          ) : null}

          <div className="flex flex-col gap-[5px]">
            <span className="flex items-baseline gap-2">
              <Label>Share image</Label>
              {image.current ? (
                <button
                  type="button"
                  disabled={image.pending}
                  onClick={() => image.onChange(null)}
                  className="btn-quiet focus-ring cursor-pointer text-[10px] disabled:cursor-default disabled:opacity-40"
                  style={{ color: 'var(--dim)' }}
                >
                  Reset to automatic
                </button>
              ) : (
                <span
                  className="rounded-full border px-1.5 font-mono text-[9px] uppercase"
                  style={{ borderColor: 'var(--line2)', color: 'var(--dim)' }}
                >
                  Automatic
                </span>
              )}
            </span>
            {/*
              The label is the box, as the cover's is: a `<label>` around a
              hidden file input makes the whole rectangle a target.
            */}
            <label
              className="focus-ring flex aspect-[1200/630] w-[180px] cursor-pointer items-center justify-center overflow-hidden rounded-[9px] border font-mono text-[10px]"
              style={{
                borderColor: 'var(--line2)',
                borderStyle: image.current ? 'solid' : 'dashed',
                background: 'var(--in)',
                color: 'var(--dim)',
              }}
            >
              <span className="sr-only">
                {image.current ? 'Replace the share image' : 'Choose a share image'}
              </span>
              {image.current ? (
                <img src={image.current} alt="" className="h-full w-full object-cover" />
              ) : (
                <span aria-hidden="true">{image.pending ? '…' : '1200 × 630'}</span>
              )}
              <input
                type="file"
                className="sr-only"
                accept="image/png,image/jpeg"
                disabled={image.pending}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Cleared at once, so the same file chosen twice still fires.
                  event.target.value = '';
                  if (file) image.onChange(file);
                }}
              />
            </label>
            {image.error ? (
              <span
                className="text-[11px] leading-[1.5]"
                role="alert"
                style={{ color: 'var(--danger)' }}
              >
                {image.error}
              </span>
            ) : (
              <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
                PNG or JPEG, 1200 × 630. Left empty, the website draws one
                {imageOnly ? ' from the headline' : ' from the page'} — the picture on the left.
              </span>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
